import { db } from "@/lib/firebase";
import { formatFirestoreDocument } from "@/lib/firestore";
import { Match } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useMemo, useRef } from "react";
import { usePageVisibility } from "./useVisibilityPausedQuery";

/**
 * Real-time hook for fetching matches with embedded results in a tournament
 * Uses Firestore onSnapshot for instant updates when matches change
 *
 * @param tournamentId - The ID of the tournament
 * @returns TanStack Query result with Match array (results embedded)
 */
export function useTournamentMatches(tournamentId: string) {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const queryKey = useMemo(
    () => ["matches", tournamentId] as const,
    [tournamentId],
  );

  // Ref to resolve the initial queryFn promise from onSnapshot
  const pendingRef = useRef<{
    resolve: (matches: Match[]) => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Set up real-time listener that continuously updates the cache
  // Pauses when page is hidden to reduce Firestore costs
  useEffect(() => {
    // Don't subscribe when page is hidden
    if (!isVisible) return;

    const matchesQuery = query(
      collection(db, "matches"),
      where("tournamentId", "==", tournamentId),
    );

    const unsubscribe = onSnapshot(
      matchesQuery,
      (snapshot) => {
        const matchesData: Match[] = snapshot.docs.map((doc) =>
          formatFirestoreDocument(doc),
        ) as Match[];

        // Resolve pending queryFn promise if waiting
        if (pendingRef.current) {
          pendingRef.current.resolve(matchesData);
          pendingRef.current = null;
        }

        queryClient.setQueryData(queryKey, matchesData);
      },
      (error) => {
        console.error("Error fetching matches:", error);
        // Reject pending promise if waiting
        if (pendingRef.current) {
          pendingRef.current.reject(error as Error);
          pendingRef.current = null;
        }
      },
    );

    return () => {
      unsubscribe();
      // Clean up pending promise on unmount - resolve with empty array instead of rejecting
      // to prevent React Query from treating this as an error
      if (pendingRef.current) {
        const cached = queryClient.getQueryData<Match[]>(queryKey);
        pendingRef.current.resolve(cached ?? []);
        pendingRef.current = null;
      }
    };
  }, [tournamentId, queryClient, queryKey, isVisible]);

  // Use the query to provide loading states and cached data
  return useQuery({
    queryKey,
    queryFn: () => {
      return new Promise<Match[]>((resolve, reject) => {
        // Check if data already exists in cache
        const cached = queryClient.getQueryData<Match[]>(queryKey);
        if (cached !== undefined) {
          resolve(cached);
          return;
        }

        // Set up timeout to prevent hanging promises
        const timeoutId = setTimeout(() => {
          if (pendingRef.current) {
            console.warn(
              "useTournamentMatches: Timeout waiting for initial data",
            );
            pendingRef.current.resolve([]);
            pendingRef.current = null;
          }
        }, 10000);

        const originalResolve = resolve;
        const originalReject = reject;

        pendingRef.current = {
          resolve: (matches) => {
            clearTimeout(timeoutId);
            originalResolve(matches);
          },
          reject: (error) => {
            clearTimeout(timeoutId);
            originalReject(error);
          },
        };
      });
    },
    staleTime: Infinity, // Real-time listener keeps it fresh
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes after unmount
  });
}
